import React from 'react';
import { X, AlertCircle, CheckCircle, Info } from 'lucide-react';

// ── Badge ──────────────────────────────────────────────────
export const Badge = ({ status, children, className = '' }) => {
  const text = children || status;
  const cls = status ? `badge badge-${status.toLowerCase().replace(/\s/g, '_')}` : 'badge';
  return <span className={`${cls} ${className}`}>{text}</span>;
};

// ── Spinner ────────────────────────────────────────────────
export const Spinner = ({ size = 20 }) => (
  <div className="spinner" style={{ width: size, height: size }} />
);

// ── Loading Page ───────────────────────────────────────────
export const LoadingPage = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
    <div style={{ textAlign: 'center' }}>
      <Spinner size={32} />
      <p style={{ marginTop: 16, color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading…</p>
    </div>
  </div>
);

// ── Empty State ────────────────────────────────────────────
export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="empty-state">
    {Icon && <Icon className="empty-state-icon" style={{ width: 60, height: 60, color: 'var(--text-muted)', opacity: 0.4 }} />}
    <div className="empty-state-title">{title}</div>
    {description && <p className="empty-state-desc">{description}</p>}
    {action}
  </div>
);

// ── Modal ──────────────────────────────────────────────────
export const Modal = ({ open, onClose, title, children, size = '' }) => {
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${size ? `modal-${size}` : ''}`}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
};

// ── Confirm Modal ──────────────────────────────────────────
export const ConfirmModal = ({ open, onClose, onConfirm, title, message, confirmLabel = 'Confirm', danger = false }) => (
  <Modal open={open} onClose={onClose} title={title}>
    <p style={{ color: 'var(--text-secondary)', marginBottom: 24, lineHeight: 1.6 }}>{message}</p>
    <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
      <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
      <button className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm}>{confirmLabel}</button>
    </div>
  </Modal>
);

// ── Alert ──────────────────────────────────────────────────
export const Alert = ({ type = 'info', children }) => {
  const configs = {
    info: { icon: Info, color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' },
    success: { icon: CheckCircle, color: 'var(--success)', bg: 'rgba(34,197,94,0.1)' },
    warning: { icon: AlertCircle, color: 'var(--warning)', bg: 'rgba(245,158,11,0.1)' },
    error: { icon: AlertCircle, color: 'var(--error)', bg: 'rgba(239,68,68,0.1)' },
  };
  const { icon: Icon, color, bg } = configs[type] || configs.info;
  return (
    <div style={{ display: 'flex', gap: 12, padding: '12px 16px', background: bg, border: `1px solid ${color}30`, borderRadius: 'var(--radius-md)', fontSize: '0.875rem', color, alignItems: 'flex-start' }}>
      <Icon size={16} style={{ marginTop: 1, flexShrink: 0 }} />
      <div>{children}</div>
    </div>
  );
};

// ── Stat Card ──────────────────────────────────────────────
export const StatCard = ({ label, value, sub, icon: Icon, color = 'gold', className = '' }) => (
  <div className={`stat-card ${color} ${className}`}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <span className="stat-label">{label}</span>
      {Icon && <Icon size={18} style={{ color: `var(--${color})`, opacity: 0.6 }} />}
    </div>
    <div className="stat-value">{value ?? '—'}</div>
    {sub && <div className="stat-sub">{sub}</div>}
  </div>
);

// ── Form components ────────────────────────────────────────
export const FormGroup = ({ label, error, children, required }) => (
  <div className="form-group">
    {label && (
      <label className="form-label">
        {label}{required && <span style={{ color: 'var(--gold)', marginLeft: 3 }}>*</span>}
      </label>
    )}
    {children}
    {error && <span className="form-error"><AlertCircle size={12} />{error}</span>}
  </div>
);

// ── Avatar ─────────────────────────────────────────────────
export const Avatar = ({ name = '', src, size = 36, role }) => {
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: 'linear-gradient(135deg, var(--gold-dim), var(--teal-deep))',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font-display)', fontSize: size * 0.36, fontWeight: 600,
      color: 'var(--gold)', flexShrink: 0, overflow: 'hidden',
      border: '1px solid rgba(201,162,39,0.2)'
    }}>
      {src ? <img src={src} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
    </div>
  );
};

// ── Tabs ───────────────────────────────────────────────────
export const Tabs = ({ tabs, active, onChange }) => (
  <div className="tabs">
    {tabs.map(tab => (
      <button
        key={tab.id}
        className={`tab ${active === tab.id ? 'active' : ''}`}
        onClick={() => onChange(tab.id)}
      >
        {tab.label}
        {tab.count != null && (
          <span style={{ marginLeft: 6, background: 'rgba(255,255,255,0.08)', padding: '1px 7px', borderRadius: 'var(--radius-full)', fontSize: '0.72rem' }}>
            {tab.count}
          </span>
        )}
      </button>
    ))}
  </div>
);

// ── Currency format ────────────────────────────────────────
export const formatCurrency = (amount, symbol = '₦') => {
  if (!amount && amount !== 0) return `${symbol}0`;
  const formatted = Number(amount).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return symbol ? `${symbol}${formatted}` : formatted;
};

// ── Date format ────────────────────────────────────────────
export const formatDate = (date, opts = {}) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', ...opts
  });
};

export const formatDateTime = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

// ── Page Header ────────────────────────────────────────────
export const PageHeader = ({ eyebrow, title, subtitle, actions }) => (
  <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
    <div>
      {eyebrow && <div className="eyebrow" style={{ marginBottom: 8 }}>{eyebrow}</div>}
      <h1 className="page-title">{title}</h1>
      {subtitle && <p className="page-subtitle">{subtitle}</p>}
    </div>
    {actions && <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>{actions}</div>}
  </div>
);

// ── Skills tags ────────────────────────────────────────────
export const SkillTags = ({ skills = [], max }) => {
  const list = max ? skills.slice(0, max) : skills;
  const rest = max ? skills.length - max : 0;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {list.map((s, i) => (
        <span key={i} style={{
          background: 'rgba(45,212,191,0.08)', color: 'var(--teal)',
          border: '1px solid rgba(45,212,191,0.15)',
          padding: '2px 10px', borderRadius: 'var(--radius-full)', fontSize: '0.75rem', fontFamily: 'var(--font-mono)'
        }}>{s}</span>
      ))}
      {rest > 0 && (
        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', lineHeight: '22px' }}>+{rest} more</span>
      )}
    </div>
  );
};

// ── Progress ───────────────────────────────────────────────
export const ProgressBar = ({ value, max = 100 }) => {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div className="progress-bar">
      <div className="progress-fill" style={{ width: `${pct}%` }} />
    </div>
  );
};
